import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap } from "lucide-react";

export default function Hero3D() {
  const canvasRef = useRef(null);
  const worldRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [typed, setTyped] = useState("");
  const fullText = "India's #1 AI Cloud";
  const animFrameRef = useRef(null);

  // Typing animation
  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      if (i <= fullText.length) {
        setTyped(fullText.slice(0, i));
        i++;
      } else {
        clearInterval(timer);
      }
    }, 80);
    return () => clearInterval(timer);
  }, []);

  // Canvas particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 90 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      size: Math.random() * 1.8 + 0.3,
      color: Math.random() > 0.5 ? "#00F3FF" : "#BC13FE",
      opacity: Math.random() * 0.6 + 0.2,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Connect close particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 243, 255, ${0.1 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.shadowBlur = 8;
        ctx.shadowColor = p.color;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
      });

      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Mouse parallax
  useEffect(() => {
    const handleMouse = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 15;
      const y = (e.clientY / window.innerHeight - 0.5) * 8;
      setMousePos({ x, y });
    };
    window.addEventListener("mousemove", handleMouse);
    return () => window.removeEventListener("mousemove", handleMouse);
  }, []);

  const leds = [
    { color: "#00FF94" }, { color: "#00F3FF" }, { color: "#BC13FE" },
    { color: "#00FF94" }, { color: "#00F3FF" }, { color: "#BC13FE" },
    { color: "#ffcc00" }, { color: "#00FF94" },
  ];

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden hero-bg animated-grid"
      style={{ paddingTop: "80px" }}
    >
      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        className="particles-canvas"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />

      {/* Radial glow spots */}
      <div className="absolute inset-0 pointer-events-none">
        <div style={{ position: "absolute", top: "10%", left: "15%", width: "300px", height: "300px", background: "radial-gradient(circle, rgba(188, 19, 254, 0.08) 0%, transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", top: "20%", right: "10%", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(0, 243, 255, 0.07) 0%, transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", bottom: "20%", left: "30%", width: "300px", height: "300px", background: "radial-gradient(circle, rgba(0, 255, 148, 0.04) 0%, transparent 70%)", borderRadius: "50%" }} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
        {/* Left: Text */}
        <div className="flex flex-col gap-6">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 w-fit px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
            style={{ background: "rgba(0, 243, 255, 0.08)", border: "1px solid rgba(0, 243, 255, 0.25)", color: "#00F3FF" }}>
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            POWERED BY AI — MADE IN INDIA
          </div>

          {/* Heading */}
          <div>
            <h1 className="font-orbitron font-900 text-5xl lg:text-6xl xl:text-7xl leading-tight mb-2 text-gradient">
              {typed}
              <span className="cursor-blink" style={{ color: "#00F3FF" }}>|</span>
            </h1>
            <h1 className="font-orbitron font-900 text-5xl lg:text-6xl xl:text-7xl leading-tight" style={{ color: "#ffffff" }}>
              Hosting
            </h1>
          </div>

          <p className="font-outfit text-base lg:text-lg text-gray-400 max-w-lg leading-relaxed" style={{ fontWeight: 300 }}>
            Ultra-fast NVMe servers. 99.9% uptime. Human Support 24x7. 
            Starting at just <span className="text-gradient font-600">₹69/month.</span> Join 50,000+ 
            Indian businesses who trust IndiaCloud.
          </p>

          {/* Stats row */}
          <div className="flex items-center gap-6 flex-wrap">
            {[
              { value: "99.9%", label: "Uptime" },
              { value: "₹69/mo", label: "Starting from" },
              { value: "10+", label: "Datacenters" },
              { value: "50K+", label: "Customers" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-orbitron font-700 text-xl neon-text-cyan">{s.value}</div>
                <div className="font-outfit text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/shared-hosting" data-testid="hero-get-started" className="no-underline">
              <button className="btn-primary flex items-center gap-2 px-8 py-3 text-sm font-700 tracking-wider">
                GET STARTED FREE <ArrowRight size={16} />
              </button>
            </Link>
            <Link to="/plan-recommender" data-testid="hero-ai-pick" className="no-underline">
              <button className="btn-neon flex items-center gap-2 px-8 py-3 text-sm">
                <Zap size={14} /> AI PICK MY PLAN
              </button>
            </Link>
          </div>
        </div>

        {/* Right: 3D Server Animation */}
        <div className="flex items-center justify-center relative" style={{ height: "480px" }}>
          {/* Orbit rings */}
          <div className="orbit-ring" style={{ width: "360px", height: "360px" }} />
          <div className="orbit-ring" style={{ width: "280px", height: "280px", animationDelay: "1s" }} />
          <div className="orbit-ring" style={{ width: "200px", height: "200px", animationDelay: "2s" }} />

          {/* 3D world container */}
          <div
            ref={worldRef}
            className="preserve-3d relative"
            style={{
              width: "260px",
              height: "340px",
              transition: "transform 0.12s ease",
              transform: `rotateX(${-mousePos.y * 0.5}deg) rotateY(${mousePos.x * 0.5}deg)`,
            }}
          >
            {/* Central server rack cube */}
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}>
              <div className="server-cube">
                {["front", "back", "left", "right", "top", "bottom"].map((face) => (
                  <div key={face} className={`cube-face ${face}`}>
                    <div className="h-full flex flex-col justify-around p-2">
                      {leds.map((led, i) => (
                        <div key={i} className="flex items-center gap-1 px-1">
                          <div
                            className="server-led"
                            style={{ color: led.color, background: led.color, animationDelay: `${i * 0.18}s` }}
                          />
                          <div className="flex-1 h-px" style={{ background: `rgba(0,243,255,0.15)` }} />
                          <div className="w-8 h-1.5 rounded-sm" style={{ background: "rgba(0,243,255,0.1)", border: "1px solid rgba(0,243,255,0.2)" }} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating cloud nodes */}
            {[
              { top: "5%", left: "-20%", delay: "0s", color: "#00F3FF" },
              { top: "15%", right: "-25%", delay: "1.5s", color: "#BC13FE" },
              { bottom: "20%", left: "-30%", delay: "2.5s", color: "#00FF94" },
              { bottom: "10%", right: "-15%", delay: "0.8s", color: "#00F3FF" },
              { top: "50%", left: "-35%", delay: "3s", color: "#BC13FE" },
            ].map((node, i) => (
              <div
                key={i}
                className="cloud-node"
                style={{
                  ...node,
                  position: "absolute",
                  width: "56px",
                  height: "36px",
                  borderColor: node.color,
                  background: `rgba(${node.color === "#00F3FF" ? "0,243,255" : node.color === "#BC13FE" ? "188,19,254" : "0,255,148"},0.06)`,
                  animationDelay: node.delay,
                  boxShadow: `0 0 12px ${node.color}33`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="text-xs font-orbitron" style={{ color: node.color, fontSize: "8px", opacity: 0.8 }}>
                  {i % 2 === 0 ? "NODE" : "API"}
                </div>
              </div>
            ))}

            {/* Neon glow underneath */}
            <div style={{
              position: "absolute",
              bottom: "-20px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "140px",
              height: "20px",
              background: "rgba(0, 243, 255, 0.2)",
              borderRadius: "50%",
              filter: "blur(10px)",
            }} />
          </div>

          {/* Status chip */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full text-xs font-rajdhani font-700"
            style={{
              background: "rgba(0, 255, 148, 0.08)",
              border: "1px solid rgba(0, 255, 148, 0.3)",
              color: "#00FF94",
              letterSpacing: "0.1em",
            }}
          >
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            ALL SYSTEMS OPERATIONAL
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-float-slow">
        <div className="text-xs text-gray-600 font-outfit tracking-widest">SCROLL</div>
        <div className="w-px h-8" style={{ background: "linear-gradient(to bottom, rgba(0,243,255,0.5), transparent)" }} />
      </div>
    </section>
  );
}
